/**
 * Native-platform helpers.
 *
 * The same React app runs in three contexts:
 *   1. Browser (web)         — all existing APIs work as before.
 *   2. Native iOS (Capacitor) — uses Capacitor plugins for mic / audio / etc.
 *   3. Native Android (Capacitor) — same.
 *
 * We detect the context once at startup and expose a tiny API so components
 * don't need to know which one they're running in.
 *
 * IMPORTANT — Apple App Store "reader app" policy:
 *   Digital goods sold on iOS MUST use In-App Purchase (Apple takes 30%).
 *   We sidestep this by: in native builds the Pricing page is hidden and
 *   subscriptions are bought on the web only. The app simply REFLECTS
 *   whatever plan the logged-in user already has. Helper
 *   `subscriptionsAllowedInApp()` returns false on native so UI renders a
 *   "Subscribe on the web" message instead of PayPal buttons.
 */
import { Capacitor } from '@capacitor/core';

/** True when running inside a Capacitor-wrapped native iOS/Android build. */
export function isNative() {
  try { return Capacitor.isNativePlatform(); } catch { return false; }
}

/** 'ios' | 'android' | 'web' */
export function platform() {
  try { return Capacitor.getPlatform(); } catch { return 'web'; }
}

export function isIOS()     { return platform() === 'ios'; }
export function isAndroid() { return platform() === 'android'; }

/**
 * Apple forbids non-IAP digital-goods purchases inside iOS apps and Google
 * Play is nearly as strict. We disable in-app subscribe UI on both native
 * platforms and route upgrade CTAs to the web instead.
 */
export function subscriptionsAllowedInApp() {
  return !isNative();
}

/** Public web URL where users subscribe from native. */
export function webUpgradeUrl() {
  const base = process.env.REACT_APP_PUBLIC_WEB_URL || process.env.REACT_APP_BACKEND_URL || '';
  return `${base.replace(/\/$/, '')}/pricing?from=app`;
}
